import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, CheckCircle2, ClipboardList, ShieldCheck, ArrowRight, MapPin, IndianRupee, Lock, Clock, AlertCircle, Send, Search } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState, LoadingState } from "../../components/shared/EmptyState";
import { studentNav } from "../../components/shared/navConfig";
import { useAuth } from "../../context/AppContext";
import { useApi, inr } from "../../lib/useApi";

const KYC_UI = {
  approved:    { icon: ShieldCheck, color: "#10B981", title: "KYC verified", desc: "You can apply to all live tasks.", cta: "View profile", to: "/student/profile", badge: "Verified" },
  pending:     { icon: Clock,       color: "#F59E0B", title: "KYC under review", desc: "Admin is reviewing your document. You'll be notified once it's done.", cta: "View profile", to: "/student/profile", badge: "Pending" },
  rejected:    { icon: AlertCircle, color: "#F43F5E", title: "KYC rejected", desc: "Please re-upload a clearer document to unlock task applications.", cta: "Re-upload KYC", to: "/student/onboarding", badge: "Rejected" },
  not_started: { icon: Lock,        color: "#F59E0B", title: "Complete KYC to apply", desc: "Upload Aadhaar or an equivalent ID to start applying for tasks.", cta: "Start KYC", to: "/student/onboarding", badge: "Pending" },
};

const ACTIVE_WR = ["Allocated", "Check-in Open", "In Progress", "Submitted", "Revision Required"];

export default function StudentDashboard() {
  const { user } = useAuth();
  const kyc = KYC_UI[user?.kyc_status || "not_started"] || KYC_UI.not_started;
  const KycIcon = kyc.icon;
  const live = useApi("/tasks/live");
  const apps = useApi("/applications/mine");
  const rooms = useApi("/workrooms/mine");
  const pay = useApi("/payments/records");
  const dw = useApi("/direct-work/mine");

  const roomsList = rooms.data || [];
  const active = roomsList.filter(r => ACTIVE_WR.includes(r.status));
  const completed = roomsList.filter(r => r.status === "Completed").length;
  const dwList = Array.isArray(dw.data) ? dw.data : (dw.data?.items || []);

  return (
    <DashboardShell nav={studentNav} title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Your workspace at a glance.">
      <div data-testid="kyc-banner" className="card-surface p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: `${kyc.color}1A`, border: `1px solid ${kyc.color}59` }}>
          <KycIcon style={{ color: kyc.color }} size={18}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium flex-wrap" style={{ color: "var(--text)" }}>{kyc.title} <StatusBadge status={kyc.badge}/></div>
          <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{kyc.desc}</div>
        </div>
        <Link to={kyc.to} data-testid="complete-profile-btn" className="btn-ghost px-4 py-2 rounded-full text-xs text-center">{kyc.cta}</Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard testId="stat-available-tasks" label="Live tasks" value={live.data?.length ?? "—"} icon={Search}/>
        <StatCard testId="stat-applications" label="Applications" value={apps.data?.length ?? "—"} icon={ClipboardList}/>
        <StatCard testId="stat-active-tasks" label="Active tasks" value={rooms.loading ? "—" : active.length} icon={Briefcase} accent/>
        <StatCard testId="stat-completed-tasks" label="Completed" value={rooms.loading ? "—" : completed} icon={CheckCircle2}/>
        <StatCard testId="stat-direct-work" label="Direct work" value={dw.loading ? "—" : dwList.length} icon={Send}/>
        <StatCard testId="stat-earnings" label="Payable earnings" value={pay.data ? inr(pay.data.totals?.Payable) : "—"} icon={IndianRupee} accent/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div><div className="eyebrow">Live tasks</div><h3 className="font-display text-lg mt-1" style={{ color: "var(--text)" }}>Recently published</h3></div>
            <Link to="/student/tasks" data-testid="view-all-tasks-link" className="text-xs text-blue-500 inline-flex items-center gap-1">View all <ArrowRight size={12}/></Link>
          </div>
          {live.loading ? <LoadingState/> : !(live.data || []).length ? (
            <EmptyState icon={Search} title="No live tasks right now" desc="New tasks appear here as soon as admin approves them." testId="dash-tasks-empty"/>
          ) : (
            <div className="space-y-2.5">
              {live.data.slice(0, 5).map(t => (
                <Link key={t.task_id} to={`/student/tasks/${t.task_id}`} data-testid={`recommended-task-${t.task_id}`}
                      className="flex items-center gap-3 p-3 rounded-md border transition-colors duration-200 hover:bg-white/5"
                      style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate" style={{ color: "var(--text)" }}>{t.title}</div>
                    <div className="text-xs truncate" style={{ color: "var(--text-dim)" }}>{t.company_name} · <MapPin size={10} className="inline"/> {t.work_mode === "Remote" ? "Remote" : (t.city || "—")}</div>
                  </div>
                  <div className="text-sm font-mono shrink-0" style={{ color: "var(--text)" }}>{inr(t.budget)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-surface p-4 sm:p-5">
          <div className="eyebrow">Active engagement</div>
          {rooms.loading ? <LoadingState/> : !active.length ? (
            <EmptyState icon={Briefcase} title="No active tasks" desc="When admin allocates you a task, your workroom shows up here." testId="dash-active-empty"/>
          ) : (
            <div className="mt-3 space-y-2.5">
              {active.slice(0, 3).map(r => (
                <Link key={r.workroom_id} to={`/workroom?id=${r.workroom_id}`} data-testid={`active-room-${r.workroom_id}`}
                      className="block p-3 rounded-md border hover:bg-white/5 transition-colors duration-200" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm min-w-0 truncate" style={{ color: "var(--text)" }}>{r.task?.title || r.task_id}</div>
                    <StatusBadge status={r.status}/>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{r.task?.company_name} · {inr(r.task?.budget)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
